import React, { useState, useRef, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  PaperAirplaneIcon as Send,
  UserIcon as User,
  DocumentDuplicateIcon as Copy,
} from "@heroicons/react/24/outline";
import { SupportChatbotMessage } from "../../services/supportChatbotService";
import { SparklesCore } from "@/components/ui/sparkles";
import { toast } from "react-hot-toast";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import FadeInText from "../FadeInText";
import TypingAnimation from "../TypingAnimation";

interface ChatbotInterfaceProps {
  messages: SupportChatbotMessage[];
  isLoading: boolean;
  isTyping: boolean;
  onSendMessage: (message: string) => void;
  messagesEndRef?: React.RefObject<HTMLDivElement>;
  isWidget?: boolean;
}

const ChatbotInterface: React.FC<ChatbotInterfaceProps> = ({
  messages,
  isLoading,
  isTyping,
  onSendMessage,
  messagesEndRef,
  isWidget = false,
}) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState("");
  const [isMobile] = useState(false);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const typingMessageRef = useRef<HTMLDivElement>(null);

  const heroStylePalette = {
    background: "#00342d",
  };

  // Handle input change - Optimized with useCallback
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setInputValue(e.target.value);
    },
    []
  );

  // Handle key down - Optimized with useCallback
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSendMessage();
      }
    },
    [onSendMessage, inputValue]
  );

  // Handle send message - Using same method as AiChat component
  const handleSendMessage = useCallback(() => {
    if (!inputValue.trim() || isLoading) return;

    onSendMessage(inputValue.trim());
    setInputValue("");
  }, [inputValue, isLoading, onSendMessage]);

  // Copy to clipboard - Optimized with useCallback
  const copyToClipboard = useCallback(
    async (text: string) => {
      try {
        await navigator.clipboard.writeText(text);
        toast.success(t("supportChatbot.copied", "Mensaje copiado"));
      } catch (error) {
        toast.error(t("common.copyError", "Error al copiar"));
      }
    },
    [t]
  );

  const formatTimestamp = (timestamp: Date) => {
    return timestamp.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Background particles component
  const backgroundParticles = useMemo(
    () => (
      <div className="absolute inset-0 w-full h-full">
        <SparklesCore
          id="support-chat-particles"
          background="transparent"
          minSize={0.4}
          maxSize={1.0}
          particleDensity={isMobile ? 80 : 120}
          className="w-full h-full"
          particleColor="#FFFFFF"
          speed={isMobile ? 0.8 : 0.7}
          interactive={false}
        />
      </div>
    ),
    [isMobile]
  );

  return (
    <div className="flex flex-col h-full" style={{ minHeight: "400px" }}>
      {/* Chat messages */}
      <div
        ref={chatContainerRef}
        className="flex-1 overflow-y-auto p-4 space-y-4"
        style={{
          backgroundColor: heroStylePalette.background,
          minHeight: "300px",
        }}
      >
        {/* Background particles */}
        {backgroundParticles}

        <div className="relative z-10 space-y-4">
          {messages.map((message, index) => (
            <div
              key={message.id || index}
              className={`flex ${
                message.sender === "user" ? "justify-end" : "justify-start"
              } group mb-4`}
            >
              <div
                className={`max-w-[85%] ${
                  message.sender === "user"
                    ? "bg-white/15 backdrop-blur-lg text-white border border-white/30"
                    : "bg-white/10 backdrop-blur-lg text-white border border-white/25"
                } rounded-2xl p-4 transition-all duration-200 hover:shadow-lg`}
              >
                {/* Message header */}
                <div className="flex justify-between items-center mb-2 pb-2 border-b border-white/20">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-white/30 backdrop-blur-sm flex items-center justify-center shadow-lg border border-white/40">
                      {message.sender === "bot" ? (
                        <img
                          src="/images/logo/NALAaiPet(1).webp"
                          alt="Support AI"
                          className="w-6 h-6 object-contain"
                        />
                      ) : (
                        <User className="h-4 w-4 text-white" />
                      )}
                    </div>
                    <div>
                      <span className="font-medium text-xs text-white">
                        {message.sender === "bot"
                          ? t("supportChatbot.aiName", "Asistente VettConsult")
                          : t("supportChatbot.you", "Tú")}
                      </span>
                      <span className="block text-xs text-white/70">
                        {formatTimestamp(message.timestamp)}
                      </span>
                    </div>
                  </div>

                  {message.sender === "bot" && message.content && (
                    <button
                      onClick={() => copyToClipboard(message.content)}
                      className="p-1 rounded-md hover:bg-white/20 transition-colors opacity-0 group-hover:opacity-100"
                    >
                      <Copy className="h-3 w-3 text-white" />
                    </button>
                  )}
                </div>

                {/* Message content */}
                {message.sender === "user" ? (
                  <div className="text-white/90 text-sm whitespace-pre-wrap">
                    {message.content}
                  </div>
                ) : (
                  <div className="text-white/90 text-sm">
                    {message.content ? (
                      <FadeInText>
                        <ReactMarkdown
                          rehypePlugins={[rehypeRaw]}
                          components={{
                            h1: ({ children }) => (
                              <h1 className="text-lg font-bold mb-2 text-white">
                                {children}
                              </h1>
                            ),
                            h2: ({ children }) => (
                              <h2 className="text-md font-semibold mb-2 text-white">
                                {children}
                              </h2>
                            ),
                            h3: ({ children }) => (
                              <h3 className="text-sm font-medium mb-2 text-white">
                                {children}
                              </h3>
                            ),
                            p: ({ children }) => (
                              <p className="mb-2 text-white/90 leading-relaxed">
                                {children}
                              </p>
                            ),
                            ul: ({ children }) => (
                              <ul className="list-disc list-inside mb-2 space-y-1 text-white/90">
                                {children}
                              </ul>
                            ),
                            li: ({ children }) => (
                              <li className="text-white/90">{children}</li>
                            ),
                            code: ({ children }) => (
                              <code className="bg-white/20 px-2 py-1 rounded text-white font-mono text-xs">
                                {children}
                              </code>
                            ),
                            strong: ({ children }) => (
                              <strong className="font-semibold text-white">
                                {children}
                              </strong>
                            ),
                            a: ({ href, children }) => (
                              <a
                                href={href}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-300 hover:text-blue-200 underline"
                              >
                                {children}
                              </a>
                            ),
                          }}
                        >
                          {message.content}
                        </ReactMarkdown>
                      </FadeInText>
                    ) : (
                      <TypingAnimation />
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}

          {/* Typing indicator */}
          {isTyping && (
            <div
              ref={typingMessageRef}
              className="flex justify-start group mb-4"
            >
              <div className="max-w-[85%] bg-white/10 backdrop-blur-lg text-white border border-white/25 rounded-2xl p-4">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-7 h-7 rounded-full bg-white/30 backdrop-blur-sm flex items-center justify-center shadow-lg border border-white/40">
                    <img
                      src="/images/logo/NALAaiPet(1).webp"
                      alt="Support AI"
                      className="w-5 h-5 object-contain animate-pulse"
                    />
                  </div>
                  <span className="text-sm text-white/90 font-medium">
                    {t("supportChatbot.aiName", "Asistente VettConsult")} está
                    escribiendo...
                  </span>
                </div>
                <TypingAnimation />
              </div>
            </div>
          )}
        </div>

        <div ref={messagesEndRef} />
      </div>

      {/* Input area - Simplified */}
      <div
        style={{
          position: "relative",
          zIndex: 1000,
          padding: "12px 16px",
          background: "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(25px)",
          borderTop: "1px solid rgba(0, 0, 0, 0.1)",
          display: "flex",
          gap: "12px",
          alignItems: "center",
          flexShrink: 0,
          boxShadow: "0 -4px 20px rgba(0, 0, 0, 0.08)",
        }}
      >
        <textarea
          placeholder={t(
            "supportChatbot.input.placeholder",
            "Type..."
          )}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          disabled={isLoading}
          rows={1}
          style={{
            flex: 1,
            background: "rgba(245, 245, 245, 0.9)",
            backdropFilter: "blur(20px)",
            border: "1px solid rgba(0, 0, 0, 0.08)",
            borderRadius: "24px",
            padding: "14px 20px",
            color: "#333333",
            fontSize: "15px",
            resize: "none",
            outline: "none",
            lineHeight: "1.4",
            boxShadow:
              "inset 0 1px 3px rgba(0, 0, 0, 0.06)",
          }}
          className="placeholder:text-gray-400"
        />
        <button
          onClick={handleSendMessage}
          disabled={isLoading || !inputValue.trim()}
          title={t("supportChatbot.send", "Enviar mensaje")}
          style={{
            background:
              isLoading || !inputValue.trim()
                ? "rgba(255, 255, 255, 0.1)"
                : "linear-gradient(135deg, #00e9be 0%, #26A69A 50%, #00d4aa 100%)",
            border: "1px solid rgba(255, 255, 255, 0.2)",
            borderRadius: "12px",
            width: "44px",
            height: "44px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: isLoading || !inputValue.trim() ? "not-allowed" : "pointer",
            flexShrink: 0,
            position: "relative",
            overflow: "hidden",
            boxShadow:
              isLoading || !inputValue.trim()
                ? "none"
                : "0 4px 15px rgba(0, 233, 190, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.3)",
            transition: "all 0.3s ease",
            backdropFilter: "blur(10px)",
          }}
          onMouseEnter={(e) => {
            if (!isLoading && inputValue.trim()) {
              e.currentTarget.style.transform = "translateY(-1px) scale(1.05)";
              e.currentTarget.style.boxShadow =
                "0 6px 20px rgba(0, 233, 190, 0.4), inset 0 1px 2px rgba(255, 255, 255, 0.3)";
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0) scale(1)";
            e.currentTarget.style.boxShadow =
              isLoading || !inputValue.trim()
                ? "none"
                : "0 4px 15px rgba(0, 233, 190, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.3)";
          }}
        >
          {/* Shine effect */}
          <div
            style={{
              position: "absolute",
              top: 0,
              left: "-100%",
              width: "100%",
              height: "100%",
              background:
                "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)",
              transition: "left 0.6s ease",
              pointerEvents: "none",
            }}
            className={!isLoading && inputValue.trim() ? "animate-pulse" : ""}
          />
          <Send
            className={`h-4 w-4 transition-all duration-300 ${
              isLoading ? "animate-spin" : ""
            } ${
              isLoading || !inputValue.trim() ? "text-white/50" : "text-white"
            }`}
          />
        </button>

        {/* Information text for widget */}
        {isWidget && (
          <div
            className="absolute bottom-1 left-1/2 transform -translate-x-1/2 text-xs text-gray-600 font-medium"
            style={{ fontSize: "10px" }}
          >
            {t("supportChatbot.widget.info", "Soporte técnico especializado")}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatbotInterface;
